'use client'

import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { IconButton, CloseIcon, ChevronRightIcon } from '@/ui'

interface PhotoLightboxProps {
  open: boolean
  /** Ids of every photo in the set, so prev/next can step through them. */
  photoIds: string[]
  index: number
  onIndexChange: (index: number) => void
  onClose: () => void
  urlFor: (photoId: string) => string
  alt: string
}

/**
 * The bigger, click-to-enlarge view of an item photo. Shows one photo full
 * size against a dark backdrop, with prev/next when the item has more than
 * one — same dismiss conventions as Dialog (Escape, backdrop click) but no
 * title bar or padding, since here the photo IS the content.
 */
export function PhotoLightbox({ open, photoIds, index, onIndexChange, onClose, urlFor, alt }: PhotoLightboxProps) {
  const scrimRef = useRef<HTMLDivElement>(null)

  // See Dialog.tsx for why these are refs rather than effect dependencies:
  // an inline onClose/onIndexChange from the parent is a new function every
  // render, and depending the effect on it directly would tear it down and
  // rebuild it (stealing focus, in Dialog's case) on every parent re-render.
  const stateRef = useRef({ onClose, onIndexChange, photoIds, index })
  useEffect(() => {
    stateRef.current = { onClose, onIndexChange, photoIds, index }
  })

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'

    const handleKeydown = (e: KeyboardEvent) => {
      const { onClose, onIndexChange, photoIds, index } = stateRef.current
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight' && index < photoIds.length - 1) onIndexChange(index + 1)
      if (e.key === 'ArrowLeft' && index > 0) onIndexChange(index - 1)
    }
    document.addEventListener('keydown', handleKeydown)
    return () => {
      document.removeEventListener('keydown', handleKeydown)
      document.body.style.overflow = ''
    }
  }, [open])

  // Warm the browser cache with the neighbouring photos so prev/next is
  // instant. Keyed on the two neighbour ids (plain strings) rather than
  // photoIds/urlFor, which are new objects on every parent render.
  const urlForRef = useRef(urlFor)
  useEffect(() => {
    urlForRef.current = urlFor
  })
  const nextId = photoIds[index + 1]
  const prevId = photoIds[index - 1]
  useEffect(() => {
    if (!open) return
    for (const id of [nextId, prevId]) {
      if (id) new Image().src = urlForRef.current(id)
    }
  }, [open, nextId, prevId])

  if (!open || photoIds.length === 0) return null

  const photoId = photoIds[index]!
  const hasPrev = index > 0
  const hasNext = index < photoIds.length - 1

  return createPortal(
    <div
      ref={scrimRef}
      onClick={(e) => {
        if (e.target === scrimRef.current) onClose()
      }}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
      className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in"
    >
      <IconButton
        icon={<CloseIcon className="w-5 h-5" />}
        label="Close"
        variant="filled"
        onClick={onClose}
        className="absolute top-4 end-4"
      />

      {hasPrev && (
        <IconButton
          icon={<ChevronRightIcon className="w-6 h-6 rotate-180" />}
          label="Previous photo"
          variant="filled"
          onClick={() => onIndexChange(index - 1)}
          className="absolute start-4 top-1/2 -translate-y-1/2"
        />
      )}
      {hasNext && (
        <IconButton
          icon={<ChevronRightIcon className="w-6 h-6" />}
          label="Next photo"
          variant="filled"
          onClick={() => onIndexChange(index + 1)}
          className="absolute end-4 top-1/2 -translate-y-1/2"
        />
      )}

      <img
        src={urlFor(photoId)}
        alt={alt}
        className="max-w-[90vw] max-h-[85vh] object-contain animate-scale-in"
        decoding="async"
      />

      {photoIds.length > 1 && (
        <div className="absolute bottom-4 start-1/2 -translate-x-1/2 text-body-sm text-white/80">
          {index + 1} / {photoIds.length}
        </div>
      )}
    </div>,
    document.body,
  )
}
